/**
 * Where the auth worker lives, and the shape of what it hands back.
 *
 * Overridable at build time with VITE_AUTH_URL so a preview deploy can point at
 * a staging auth worker.
 */

export const AUTH_URL: string = import.meta.env.VITE_AUTH_URL ?? "https://auth.railassurance.co.uk";

/**
 * The auth worker's login page. It takes no redirect parameter — it decides
 * where to send the user server-side from their org membership (org members go
 * to the platform, everyone else lands back here at /auth/callback).
 */
export const LOGIN_URL = `${AUTH_URL}/login`;

/** Endpoints on the auth worker. All are called with credentials included so
 * the HttpOnly refresh cookie on the auth domain rides along. */
export const AUTH_ENDPOINTS = {
  exchange: `${AUTH_URL}/api/auth/exchange`,
  refresh: `${AUTH_URL}/api/auth/refresh`,
  logout: `${AUTH_URL}/api/auth/logout`,
  /** Switch an existing session to another RailAssurance surface. */
  switchTo: (target: "tools" | "app") => `${AUTH_URL}/switch?target=${target}`,
} as const;

/** Response body of /api/auth/exchange and /api/auth/refresh. */
export interface TokenResponse {
  ok: true;
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
  user?: {
    id: string;
    email: string;
    name: string | null;
    tier: string;
    org_id: string | null;
    org_name: string | null;
  };
}

/** Where to send the user after login completes. */
const RETURN_TO_KEY = "ra:returnTo";

export function rememberReturnTo(path: string): void {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  } catch {
    // Private browsing / storage disabled — we just lose the deep link.
  }
}

/** Consume the stored return path. Only ever returns a same-origin path. */
export function takeReturnTo(): string {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // ignore
  }
  // Reject anything that could redirect off-origin (protocol-relative, absolute).
  if (!stored || !stored.startsWith("/") || stored.startsWith("//")) return "/";
  if (stored.startsWith("/auth/callback")) return "/";
  return stored;
}
